/**
 * RoleAssignmentModal Component
 * Modal for changing a user's role
 */

import { useState } from 'react'
import { X, Shield, AlertCircle, AlertTriangle, Check } from 'lucide-react'
import type { User, UserRole } from '@/types/user.types'
import { getRoleDisplayName, getRoleBadgeClass } from '@/types/auth.types'
import { ROLE_HIERARCHY } from '@/types/user.types'

// ============================================================================
// TYPES
// ============================================================================

interface RoleAssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (userId: string, newRole: UserRole) => Promise<void>
  user: User | null
  availableRoles?: UserRole[]
  currentUserRole?: UserRole
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_ROLES: UserRole[] = [
  'firm_admin',
  'ca_partner',
  'technical_assistant',
  'research_assistant',
  'individual_client',
  'business_owner_client',
  'authorized_client',
]

const ROLE_DESCRIPTIONS: Partial<Record<UserRole, string>> = {
  firm_admin: 'User and security administration without billing ownership',
  ca_partner: 'Portfolio management, filings, reviews, and approvals',
  technical_assistant: 'Assigned tax preparation and document handling',
  research_assistant: 'Research and advisory support without filing authority',
  individual_client: 'Self-service access to one taxpayer relationship',
  business_owner_client: 'Access to explicitly linked business entities',
  authorized_client: 'Time-bound and action-scoped representative access',
}

const ROLE_PERMISSIONS_SUMMARY: Partial<Record<UserRole, string[]>> = {
  firm_admin: ['Manage users', 'View audit logs', 'Manage settings'],
  ca_partner: ['Manage portfolios', 'Approve assessments', 'Export reports'],
  technical_assistant: ['Prepare assessments', 'Upload documents', 'Work assigned clients'],
  research_assistant: ['Prepare research', 'Draft advisory notes', 'Request review'],
  individual_client: ['View own filing', 'Upload own documents'],
  business_owner_client: ['View linked entities', 'Manage representatives'],
  authorized_client: ['View granted records', 'Perform granted actions'],
}

// ============================================================================
// COMPONENT
// ============================================================================

export function RoleAssignmentModal({
  isOpen,
  onClose,
  onSubmit,
  user,
  availableRoles = DEFAULT_ROLES,
  currentUserRole,
}: RoleAssignmentModalProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Reset when modal opens with new user
  useState(() => {
    if (user) {
      setSelectedRole(user.role)
    }
  })

  const handleSubmit = async () => {
    if (!user || !selectedRole) return

    if (selectedRole === user.role) {
      onClose()
      return
    }

    setError('')
    setIsSubmitting(true)

    try {
      await onSubmit(user.userId, selectedRole)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change role')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Check if this is a promotion or demotion
  const isPromotion =
    user && selectedRole
      ? ROLE_HIERARCHY[selectedRole] < ROLE_HIERARCHY[user.role]
      : false
  const isDemotion =
    user && selectedRole
      ? ROLE_HIERARCHY[selectedRole] > ROLE_HIERARCHY[user.role]
      : false

  // Check if user can assign this role (can't assign higher than own)
  const canAssignRole = (role: UserRole) => {
    if (!currentUserRole) return true
    return ROLE_HIERARCHY[role] <= ROLE_HIERARCHY[currentUserRole]
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-dark rounded-xl shadow-2xl border border-surface-overlay w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-overlay">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Change Role</h2>
              <p className="text-sm text-text-tertiary">Update role for {user.fullName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Error Alert */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Current User Info */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-overlay/50">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-sm font-medium">
              {user.fullName
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </div>
            <div className="flex-1">
              <p className="font-medium text-text-primary">{user.fullName}</p>
              <p className="text-sm text-text-tertiary">{user.email}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-text-tertiary">Current Role</p>
              <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                {getRoleDisplayName(user.role)}
              </span>
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Select New Role
            </label>
            <div className="space-y-2">
              {availableRoles.map((role) => {
                const isSelected = selectedRole === role
                const isCurrent = user.role === role
                const canAssign = canAssignRole(role)

                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => canAssign && setSelectedRole(role)}
                    disabled={!canAssign || isSubmitting}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : canAssign
                          ? 'border-surface-overlay hover:border-primary-300 hover:bg-surface-overlay/50'
                          : 'border-surface-overlay bg-surface-overlay/30 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`badge ${getRoleBadgeClass(role)} badge-sm`}>
                            {getRoleDisplayName(role)}
                          </span>
                          {isCurrent && (
                            <span className="badge badge-outline badge-xs">Current</span>
                          )}
                        </div>
                        <p className="text-sm text-text-secondary mt-1">
                          {ROLE_DESCRIPTIONS[role]}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(ROLE_PERMISSIONS_SUMMARY[role] ?? []).slice(0, 3).map((perm, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-0.5 rounded bg-surface-overlay text-text-tertiary"
                            >
                              {perm}
                            </span>
                          ))}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 ml-3">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Warning for promotion/demotion */}
          {selectedRole && selectedRole !== user.role && (
            <div
              className={`p-3 rounded-lg flex items-start gap-2 ${
                isPromotion
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  : isDemotion
                    ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                    : ''
              }`}
            >
              <AlertTriangle
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  isPromotion ? 'text-amber-600' : 'text-blue-600'
                }`}
              />
              <div>
                <p
                  className={`text-sm font-medium ${
                    isPromotion
                      ? 'text-amber-700 dark:text-amber-300'
                      : 'text-blue-700 dark:text-blue-300'
                  }`}
                >
                  {isPromotion ? 'This is a promotion' : 'This is a role change'}
                </p>
                <p
                  className={`text-xs mt-1 ${
                    isPromotion
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-blue-600 dark:text-blue-400'
                  }`}
                >
                  {isPromotion
                    ? `${user.fullName} will gain additional permissions with this role.`
                    : isDemotion
                      ? `${user.fullName} will lose some permissions with this role.`
                      : `This will change ${user.fullName}'s access level.`}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-surface-overlay bg-surface-overlay/30">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={isSubmitting || !selectedRole || selectedRole === user.role}
          >
            {isSubmitting ? (
              <>
                <span className="loading loading-spinner loading-sm" />
                Updating...
              </>
            ) : (
              'Update Role'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RoleAssignmentModal
